import { restClient } from '@/Core'
import { userManager } from '@/Core/Managers'

/** Валюта сум власника CRM за замовчуванням (`SPEC-008` §4.8, R-02 перевізника). */
export const CRM_DEFAULT_CURRENCY_CODE = 'EUR'

/** Валюта так, як її віддає картка валюти ядра. */
interface CurrencyRef {
    '@id'?: string
    uuid?: string
    code?: string
    title?: string
}

/** Те, що задача бере з ядрового `formContext` форми створення. */
export interface CrmOwnerCreateFormContextLike {
    values?: Record<string, unknown>
    setInitialValue?: (path: string, value: unknown) => void
}

let pendingCurrency: Promise<CurrencyRef | null> | null = null

/**
 * Валюта за замовчуванням: `GET /services/currencies/EUR` — ядро адресує
 * валюту кодом (`SPEC-007` R-21), зашитих ідентифікаторів, як у легасі
 * (`DefaultValueConfig.js`), немає. Валюти немає — `null`: бек зробить так
 * само (`CrmDefaultCurrencyResolver`).
 *
 * Один запит на сесію: довідник за час роботи з формою не зміниться.
 * Невдалий запит не кешуємо — наступна форма спробує знову.
 */
const loadDefaultCurrency = (): Promise<CurrencyRef | null> => {
    pendingCurrency ??= (async () => {
        try {
            const currency = (await restClient.get(
                `/services/currencies/${CRM_DEFAULT_CURRENCY_CODE}`
            )) as unknown as CurrencyRef | null

            return currency && typeof currency === 'object' ? currency : null
        } catch {
            pendingCurrency = null

            return null
        }
    })()

    return pendingCurrency
}

const isBlank = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/**
 * Поточний користувач — значенням поля «Відповідальний». Select ядра впізнає
 * вибране за `@id`, а бек приймає зв'язок за IRI, тож без `@id` будуємо його з
 * `uuid` — так само, як його будує колекція співробітників.
 */
const currentUserRef = (): Record<string, unknown> | null => {
    const user = userManager.user as Record<string, unknown>
    const uuid = typeof user['uuid'] === 'string' ? user['uuid'] : null

    if (!uuid) return null

    return { ...user, '@id': typeof user['@id'] === 'string' ? user['@id'] : `/services/users/${uuid}`, uuid }
}

/**
 * Значення форми створення власника CRM:
 *
 *  - **«Відповідальний» — той, хто створює**: у легасі так робила сторінка
 *    (`CarrierPage.jsx:58-62`), а бек тепер вимагає хоча б одного (R-07);
 *  - **валюта сум — EUR** (R-02): той самий дефолт ставить і бек, але без
 *    нього у формі валюта стояла б порожньою, і людина не бачила б, у чому
 *    вводить суму.
 *
 * Пише лише в порожні поля й через `setInitialValue`: вибране людиною не
 * перезаписується, а значення за замовчуванням не рахується незбереженою
 * зміною.
 */
export const ApplyCrmOwnerCreateDefaultsTask = async (
    formContext: CrmOwnerCreateFormContextLike,
    currencyFields: readonly string[]
): Promise<void> => {
    if (isBlank(formContext.values?.['responsibles'])) {
        const user = currentUserRef()

        if (user) formContext.setInitialValue?.('responsibles', [user])
    }

    if (currencyFields.length === 0) return

    const currency = await loadDefaultCurrency()

    if (!currency) return

    for (const field of currencyFields) {
        if (isBlank(formContext.values?.[field])) formContext.setInitialValue?.(field, currency)
    }
}
